import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Sparkles, Loader2, Users, PhoneCall, ShieldAlert, Target, ChevronDown, ChevronRight, Search, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Markdown from "@/components/common/Markdown";
import MultiBrandSelect from "@/components/common/MultiBrandSelect";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import DatePicker from "@/components/common/DatePicker";
import ConvertedUsersDrilldown, { ConvertedCountButton } from "@/components/ops/ConvertedUsersDrilldown";
import { DEFAULT_OPS_RANGE } from "@/lib/dateDefaults";
import { useAuth } from "@/lib/auth";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

const fmt = (n) => (n || 0).toLocaleString();

/** Small "Data as of X min ago" banner + optional Rebuild button (admins).
 * The KPIs / leaderboards on this page read from the pre-aggregated
 * `ops_daily_agg` collection which is rebuilt at upload/purge time. This
 * banner tells the operator how fresh that pre-aggregate is. */
function FreshnessBanner({ status, isAdmin }) {
  const [busy, setBusy] = useState(false);
  const last = status?.last_rebuilt_at ? new Date(status.last_rebuilt_at) : null;

  const relative = useMemo(() => {
    if (!last) return null;
    const s = Math.max(0, Math.round((Date.now() - last.getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)} min ago`;
    if (s < 86400) return `${Math.round(s / 3600)} hr ago`;
    return `${Math.round(s / 86400)}d ago`;
  }, [last]);

  const rebuildAll = async () => {
    if (!window.confirm("Rebuild pre-aggregated dashboard data for the last 90 days?\n\nThis takes a few seconds and will not disrupt live queries.")) return;
    setBusy(true);
    try {
      const to = new Date();
      const from = new Date(); from.setDate(from.getDate() - 90);
      const fmtDate = (d) => d.toISOString().slice(0, 10);
      const { data } = await api.post("/admin/rebuild-rollup", {
        date_from: fmtDate(from), date_to: fmtDate(to),
      });
      window.location.reload();  // reload will refetch dashboards + banner
      return data;
    } finally { setBusy(false); }
  };

  const isStale = last && (Date.now() - last.getTime()) > 30 * 60 * 1000; // 30 min

  return (
    <div
      className={`glass-card px-4 py-2 flex items-center justify-between gap-3 ${
        isStale ? "border border-amber-300 bg-amber-50/70" : ""
      }`}
      data-testid="ops-freshness-banner"
    >
      <div className="text-xs text-slate-600 flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${isStale ? "bg-amber-500" : "bg-emerald-500"}`}></span>
        {last ? (
          <>
            Data as of <b className="text-slate-900">{relative}</b>
            <span className="text-slate-400 ml-1">({last.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })})</span>
          </>
        ) : (
          <>Pre-aggregated dashboard cache not yet built. Run a manual rebuild or complete an upload.</>
        )}
      </div>
      {isAdmin && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={rebuildAll}
          disabled={busy}
          data-testid="ops-rebuild-rollup-btn"
        >
          {busy ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Rebuilding…</> : "Rebuild now"}
        </Button>
      )}
    </div>
  );
}


function Kpi({ label, value, icon: Icon, sub, ...rest }) {
  return (
    <div className="glass-card p-5" {...rest}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="kpi-num text-3xl mt-1 text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

/** Filter row helper — consistent label above every control */
function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</Label>
      {children}
    </div>
  );
}

export default function OpsDashboardPage() {
  const { user } = useAuth();
  const isPrivileged = !!(user?.is_admin || user?.is_super_admin);
  const lockedTeamId = !isPrivileged && user?.team_id ? user.team_id : null;
  const _range = DEFAULT_OPS_RANGE();
  const [ctx, setCtx] = useState({ brands: [], teams: [], campaigns: [] });
  const [brandIds, setBrandIds] = useState([]); // [] = all
  const [teamId, setTeamId] = useState(lockedTeamId || "all");
  const [campaignId, setCampaignId] = useState("all");
  const [dateFrom, setDateFrom] = useState(_range.from);
  const [dateTo, setDateTo] = useState(_range.to);
  const [kpis, setKpis] = useState(null);
  const [agents, setAgents] = useState([]);
  const [teamRows, setTeamRows] = useState([]);
  const [campaignLb, setCampaignLb] = useState([]);
  const [agentsOpen, setAgentsOpen] = useState(false);
  const [agentQuery, setAgentQuery] = useState("");
  const [dispositions, setDispositions] = useState([]);
  const [heat, setHeat] = useState({ dates: [], dispositions: [], grid: {} });
  const [anom, setAnom] = useState([]);
  const [ai, setAi] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drilldown, setDrilldown] = useState({ open: false });
  const [refreshStatus, setRefreshStatus] = useState(null);

  // ---------------- Sortable tables ----------------
  // Filter agents by search, then feed into sortable hook.
  const filteredAgents = useMemo(() => {
    const q = agentQuery.trim().toLowerCase();
    return q
      ? agents.filter((a) => (a.agent_name || "").toLowerCase().includes(q)
          || String(a.agent_id || "").toLowerCase().includes(q))
      : agents;
  }, [agents, agentQuery]);

  const { rows: agentsSorted, thProps: aThProps } = useSortableTable(filteredAgents, {
    initialKey: "total_converted", initialDir: "desc",
    accessors: { agent_name: (r) => (r.agent_name || "").toLowerCase() },
  });

  const { rows: teamRowsSorted, thProps: tThProps } = useSortableTable(teamRows, {
    initialKey: "total_converted", initialDir: "desc",
    accessors: { team_name: (r) => (r.team_name || "").toLowerCase() },
  });

  const { rows: campaignRowsSorted, thProps: cThProps } = useSortableTable(campaignLb, {
    initialKey: "total_converted", initialDir: "desc",
    accessors: {
      campaign_name: (r) => (r.campaign_name || "").toLowerCase(),
      brand_name: (r) => (r.brand_name || "").toLowerCase(),
    },
  });

  // Open the drill-down with the current global filters + a specific kind/agent.
  // Pass agent context so the drawer's title reads e.g. "SMS-credited · Agent: Rahul"
  const openDrilldown = (kind, extra = {}) => {
    setDrilldown({
      open: true,
      kind,
      brand_id: params.brand_id,
      date_from: params.date_from,
      date_to: params.date_to,
      team_id: params.team_id,
      campaign_id: params.campaign_id,
      ...extra,
    });
  };

  useEffect(() => { api.get("/ops/my-context").then((r) => setCtx(r.data)); }, []);

  const params = useMemo(() => {
    const p = {};
    if (brandIds.length === 1) p.brand_id = brandIds[0];
    if (teamId !== "all") p.team_id = teamId;
    if (campaignId !== "all") p.campaign_id = campaignId;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [brandIds, teamId, campaignId, dateFrom, dateTo]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/ops/analytics/kpis", { params }).then((r) => setKpis(r.data)),
      api.get("/ops/analytics/agents", { params }).then((r) => setAgents(r.data)),
      api.get("/ops/analytics/team-leaderboard", { params }).then((r) => setTeamRows(r.data)),
      api.get("/ops/analytics/campaign-leaderboard", { params }).then((r) => setCampaignLb(r.data)),
      api.get("/ops/analytics/dispositions", { params }).then((r) => setDispositions(r.data)),
      api.get("/ops/analytics/heatmap", { params }).then((r) => setHeat(r.data)),
      api.get("/ops/analytics/anomalies", { params }).then((r) => setAnom(r.data)),
      api.get("/ops/analytics/refresh-status").then((r) => setRefreshStatus(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    setAi("");
  }, [params]);

  /* Client-side filter for multi-brand — API takes one brand at a time,
     so when 2+ selected we filter agents/dispos client-side by brand membership.
     For counts/heatmap we currently show "all" when >1 brand selected — noted in UI. */
  const brandFilterActive = brandIds.length >= 2;

  const generateAi = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/ops/analytics/ai-digest", null, { params });
      setAi(r.data.insight_markdown || "");
    } finally { setAiBusy(false); }
  };

  const heatMax = useMemo(() => {
    let m = 0;
    for (const d of heat.dates) for (const dp of heat.dispositions) {
      const v = heat.grid[d]?.[dp] || 0;
      if (v > m) m = v;
    }
    return m || 1;
  }, [heat]);

  // Totals row: sums numeric columns and recomputes weighted rate columns
  // from the summed numerator/denominator (correct math — a simple average
  // of percentages would over-weight low-volume rows).
  const computeTotals = (rows) => {
    if (!rows?.length) return null;
    const sum = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    const total_calls = sum("total_calls");
    const contactable = sum("contactable");
    const converted = sum("converted");
    const sms_converted = sum("sms_converted");
    const wn_converted = sum("wn_converted");
    const invalid_converted = sum("invalid_converted");
    const total_converted = sum("total_converted");
    const fresh = sum("fresh");
    const followup = sum("followup");
    const wrong_number = rows.reduce(
      (a, r) => a + Math.round(((Number(r.wrong_number_pct) || 0) / 100) * (Number(r.total_calls) || 0)),
      0,
    );
    return {
      total_calls, contactable, converted, sms_converted,
      wn_converted, invalid_converted, total_converted, fresh, followup,
      contactable_pct: total_calls ? (contactable / total_calls) * 100 : 0,
      conversion_of_contactable_pct: contactable ? (converted / contactable) * 100 : 0,
      sms_conversion_pct: total_calls ? (sms_converted / total_calls) * 100 : 0,
      total_conversion_pct: total_calls ? (total_converted / total_calls) * 100 : 0,
      conversion_pct: total_calls ? (converted / total_calls) * 100 : 0,
      wrong_number_pct: total_calls ? (wrong_number / total_calls) * 100 : 0,
    };
  };

  return (
    <div className="space-y-6">
      <FreshnessBanner status={refreshStatus} isAdmin={isPrivileged} />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">Ops dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Calling performance, agent leaderboard, dispositions & anomalies.</p>
        </div>
        <div className="flex gap-3 flex-wrap items-end" data-testid="ops-filter-row">
          <Field label="Brand">
            <MultiBrandSelect
              brands={ctx.brands}
              value={brandIds}
              onChange={setBrandIds}
              testId="ops-brand-multi"
            />
          </Field>
          {(ctx.teams?.length > 0) && (
            <Field label="Team">
              {lockedTeamId ? (
                <div
                  className="glass-input h-9 w-[180px] px-3 flex items-center gap-2 text-sm text-slate-600 bg-slate-100/70 cursor-not-allowed"
                  title="You can only view your assigned team."
                  data-testid="ops-team-locked"
                >
                  <Lock className="w-3.5 h-3.5 text-slate-400" />
                  {(ctx.teams.find((t) => t.id === lockedTeamId) || {}).name || "Your team"}
                </div>
              ) : (
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger className="glass-input h-9 w-[180px]" data-testid="ops-team-select">
                    <SelectValue placeholder="All teams" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All teams</SelectItem>
                    {(ctx.teams || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}
          {(ctx.campaigns?.length > 0) && (
            <Field label="Campaign">
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger className="glass-input h-9 w-[200px]" data-testid="ops-campaign-select">
                  <SelectValue placeholder="All campaigns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All campaigns</SelectItem>
                  {(ctx.campaigns || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="From">
            <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="Pick from date"
              className="w-[170px]" testId="ops-filter-from" />
          </Field>
          <Field label="To">
            <DatePicker value={dateTo} onChange={setDateTo} placeholder="Pick to date"
              className="w-[170px]" testId="ops-filter-to" />
          </Field>
        </div>
      </div>

      {brandFilterActive && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 inline-block">
          Multi-brand filter: showing aggregate across all your brands. Select exactly one brand to scope numbers to it.
        </div>
      )}

      {/* AI daily digest — moved to top so it's not missed */}
      <div className="glass-card p-5" data-testid="ops-ai-digest">
        <div className="flex items-center justify-between mb-3">
          <div className="font-outfit font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#0A84FF]" /> AI daily digest — cross-report
          </div>
          <Button size="sm" onClick={generateAi} disabled={aiBusy} data-testid="ops-ai-digest-btn"
                  className="btn-tactile" style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}>
            {aiBusy ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Generating…</> : "Generate"}
          </Button>
        </div>
        <div className="text-sm text-slate-800 max-h-[520px] overflow-auto">
          {aiBusy ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Anthropic Claude is analysing 7 report signals…
            </div>
          ) : ai ? (
            <Markdown>{ai}</Markdown>
          ) : (
            <p className="text-slate-500 text-sm">
              Click <b>Generate</b> for a Claude-authored narrative that combines <b>KPIs · agent scorecard · dispositions · funnel · retention · TTC · peak-hours · data quality</b> for the selected period — one unified analysis, not eight disjointed panels.
            </p>
          )}
        </div>
      </div>

      {/* KPIs */}
      <LoadingOverlay loading={loading} testId="ops-kpis-loading">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Kpi
            label="Total calls"
            value={fmt(kpis?.total_calls)}
            icon={PhoneCall}
            sub={`${fmt(kpis?.contactable || 0)} contactable · ${(kpis?.contactable_pct || 0).toFixed(1)}%`}
            data-testid="kpi-total-calls"
          />
          <Kpi
            label="Total conversions"
            value={
              <ConvertedCountButton
                value={kpis?.total_converted}
                kind="total"
                onOpen={(k) => openDrilldown(k)}
                testId="kpi-total-converted-drill"
                className="kpi-num text-3xl mt-1 text-slate-900"
              />
            }
            icon={Target}
            sub={`${(kpis?.total_conversion_pct || 0).toFixed(1)}% of dialled · Call ${fmt(kpis?.converted || 0)} + SMS ${fmt(kpis?.sms_converted || 0)} + WN ${fmt(kpis?.wn_converted || 0)} + Inv ${fmt(kpis?.invalid_converted || 0)}`}
            data-testid="kpi-total-converted"
          />
          <Kpi
            label="Call conversions"
            value={
              <ConvertedCountButton
                value={kpis?.converted}
                kind="call"
                onOpen={(k) => openDrilldown(k)}
                testId="kpi-call-converted-drill"
                className="kpi-num text-3xl mt-1 text-slate-900"
              />
            }
            icon={Target}
            sub={`${(kpis?.conversion_of_contactable_pct || 0).toFixed(1)}% of contactable · ${(kpis?.conversion_pct || 0).toFixed(1)}% of dialled`}
            data-testid="kpi-conversions"
          />
          <Kpi
            label="SMS conv · WN → Conv"
            value={
              <span className="kpi-num text-3xl mt-1 text-slate-900 flex items-baseline gap-2">
                <ConvertedCountButton
                  value={kpis?.sms_converted}
                  kind="sms"
                  onOpen={(k) => openDrilldown(k)}
                  testId="kpi-sms-drill"
                />
                <span className="text-slate-400">·</span>
                <ConvertedCountButton
                  value={kpis?.wn_converted}
                  kind="wn"
                  onOpen={(k) => openDrilldown(k)}
                  testId="kpi-wn-drill"
                />
              </span>
            }
            icon={Target}
            sub={
              <>
                Invalid → Conv:{" "}
                <ConvertedCountButton
                  value={kpis?.invalid_converted || 0}
                  kind="invalid"
                  onOpen={(k) => openDrilldown(k)}
                  testId="kpi-invalid-drill"
                  className="inline"
                />
                {kpis?.sms_conversion_pct ? ` · SMS ${(kpis?.sms_conversion_pct || 0).toFixed(1)}%` : ""}
              </>
            }
            data-testid="kpi-offline-conv"
          />
          <Kpi label="Active agents" value={fmt(kpis?.active_agents)} icon={Users} />
          <Kpi label="Fresh / Follow-up" value={`${fmt(kpis?.fresh)} / ${fmt(kpis?.followup)}`} icon={PhoneCall} />
        </div>
      </LoadingOverlay>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Disposition bar */}
        <LoadingOverlay loading={loading}>
          <div className="glass-card p-5 lg:col-span-2">
            <div className="font-outfit font-semibold text-slate-900 mb-3">Dispositions</div>
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={dispositions}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                  <XAxis dataKey="disposition" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0A84FF" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </LoadingOverlay>

        {/* Anomalies */}
        <LoadingOverlay loading={loading}>
          <div className="glass-card p-5">
            <div className="font-outfit font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600" /> Anomaly flags
            </div>
            {anom.length === 0 ? (
              <div className="text-sm text-slate-500">No anomalies detected.</div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-auto">
                {anom.map((a, i) => (
                  <div key={i} className="glass-solid p-3">
                    <div className="text-sm font-medium text-slate-900">{a.agent_name}</div>
                    <div className="text-xs text-slate-600">{a.detail}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </LoadingOverlay>
      </div>

      {/* Agent leaderboard — collapsed by default; shows top 10 + expand */}
      <LoadingOverlay loading={loading}>
        <div className="glass-card p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <button
              type="button"
              onClick={() => setAgentsOpen((o) => !o)}
              className="flex items-center gap-2 font-outfit font-semibold text-slate-900 hover:text-[#0A84FF] transition"
              data-testid="agent-leaderboard-toggle"
            >
              {agentsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Agent leaderboard
              <span className="text-xs font-normal text-slate-500 ml-1">({agents.length} agents)</span>
            </button>
            {agentsOpen && (
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
                <input
                  type="text"
                  value={agentQuery}
                  onChange={(e) => setAgentQuery(e.target.value)}
                  placeholder="Filter agents…"
                  data-testid="agent-leaderboard-search"
                  className="glass-input h-8 pl-7 pr-2 text-xs w-56"
                />
              </div>
            )}
          </div>
          {(() => {
            const shown = agentsOpen ? agentsSorted : agentsSorted.slice(0, 10);
            const hiddenCount = agentsSorted.length - shown.length;
            return (
              <div className="overflow-auto max-h-[70vh]">
                <table className="w-full text-sm">
                  <thead data-sticky>
                    <tr className="text-slate-500 text-xs uppercase">
                      <SortableTh {...aThProps("agent_name", "left")}>Agent</SortableTh>
                      <SortableTh {...aThProps("total_calls")}>Calls</SortableTh>
                      <SortableTh {...aThProps("contactable")}>Contactable</SortableTh>
                      <SortableTh {...aThProps("contactable_pct")} title="Contactable ÷ Calls">Contact %</SortableTh>
                      <SortableTh {...aThProps("converted")}>Converted</SortableTh>
                      <SortableTh {...aThProps("total_converted")} className="bg-slate-50" title="Total across all credit channels: Call + SMS + WN→Conv + Inv→Conv.">Total conv</SortableTh>
                      <SortableTh {...aThProps("conversion_of_contactable_pct")} title="Converted ÷ Contactable">Conv % (contactable)</SortableTh>
                      <SortableTh {...aThProps("sms_converted")} title="Deposits credited to an SMS/voicemail nudge">SMS conv</SortableTh>
                      <SortableTh {...aThProps("wn_converted")} title="Deposits after 'wrong number' disposition">WN → Conv</SortableTh>
                      <SortableTh {...aThProps("invalid_converted")} title="Deposits after 'invalid number' disposition">Inv → Conv</SortableTh>
                      <SortableTh {...aThProps("conversion_pct")} title="Converted ÷ Total calls — legacy metric">Conv % (dialled)</SortableTh>
                      <SortableTh {...aThProps("fresh")}>Fresh / Follow</SortableTh>
                      <SortableTh {...aThProps("wrong_number_pct")} title="Percent of calls dispositioned 'wrong number' (>30% is unusual).">Wrong number %</SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((a) => (
                      <tr key={a.agent_id || a.agent_name} className="border-t border-white/60" data-testid={`agent-row-${a.agent_id || a.agent_name}`}>
                        <td className="py-2 px-2 font-medium">
                          {a.agent_id ? (
                            <Link
                              to={`/ops/agents/${encodeURIComponent(a.agent_id)}`}
                              className="hover:underline text-[#0A84FF]"
                              data-testid={`agent-link-${a.agent_id}`}
                              title="Open agent profile — audit their conversions in detail"
                            >
                              {a.agent_name}
                            </Link>
                          ) : (
                            a.agent_name
                          )}
                        </td>
                        <td className="py-2 px-2 text-right">{a.total_calls}</td>
                        <td className="py-2 px-2 text-right">{a.contactable}</td>
                        <td className="py-2 px-2 text-right">{(a.contactable_pct ?? 0).toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right">
                          <ConvertedCountButton
                            value={a.converted}
                            kind="call"
                            onOpen={(k) => openDrilldown(k, { agent_id: a.agent_id, agent_name: a.agent_name })}
                            testId={`agent-converted-drill-${a.agent_id || a.agent_name}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-right bg-slate-50 font-semibold text-slate-900" data-testid={`agent-total-${a.agent_id || a.agent_name}`}>
                          <ConvertedCountButton
                            value={a.total_converted || 0}
                            kind="total"
                            onOpen={(k) => openDrilldown(k, { agent_id: a.agent_id, agent_name: a.agent_name })}
                            testId={`agent-total-drill-${a.agent_id || a.agent_name}`}
                          />
                          {a.total_calls > 0 && (
                            <span className="text-slate-400 text-[10px] ml-1">({(a.total_conversion_pct ?? 0).toFixed(1)}%)</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right font-semibold text-emerald-700">{(a.conversion_of_contactable_pct ?? 0).toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right text-sky-700" data-testid={`agent-sms-${a.agent_id || a.agent_name}`}>
                          <ConvertedCountButton
                            value={a.sms_converted || 0}
                            kind="sms"
                            onOpen={(k) => openDrilldown(k, { agent_id: a.agent_id, agent_name: a.agent_name })}
                            testId={`agent-sms-drill-${a.agent_id || a.agent_name}`}
                          />
                          {a.total_calls > 0 && (
                            <span className="text-slate-400 text-[10px] ml-1">({(a.sms_conversion_pct ?? 0).toFixed(1)}%)</span>
                          )}
                        </td>
                        <td className={`py-2 px-2 text-right ${(a.wn_converted || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`} data-testid={`agent-wn-${a.agent_id || a.agent_name}`}>
                          <ConvertedCountButton
                            value={a.wn_converted || 0}
                            kind="wn"
                            onOpen={(k) => openDrilldown(k, { agent_id: a.agent_id, agent_name: a.agent_name })}
                            testId={`agent-wn-drill-${a.agent_id || a.agent_name}`}
                          />
                        </td>
                        <td className={`py-2 px-2 text-right ${(a.invalid_converted || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>
                          <ConvertedCountButton
                            value={a.invalid_converted || 0}
                            kind="invalid"
                            onOpen={(k) => openDrilldown(k, { agent_id: a.agent_id, agent_name: a.agent_name })}
                            testId={`agent-invalid-drill-${a.agent_id || a.agent_name}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-right text-slate-500">{(a.conversion_pct ?? 0).toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right">{a.fresh}/{a.followup}</td>
                        <td className={`py-2 px-2 text-right ${a.wrong_number_pct > 30 ? "text-red-600 font-semibold" : ""}`}>{a.wrong_number_pct}%</td>
                      </tr>
                    ))}
                    {shown.length === 0 && (
                      <tr><td colSpan={13} className="text-center py-4 text-slate-400">
                        {agentQuery.trim() ? "No agents match your filter." : "No agents yet."}
                      </td></tr>
                    )}
                  </tbody>
                  {shown.length > 0 && (() => {
                    const t = computeTotals(shown);
                    if (!t) return null;
                    return (
                      <tfoot>
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="agent-leaderboard-totals">
                          <td className="py-2 px-2">Total</td>
                          <td className="py-2 px-2 text-right">{fmt(t.total_calls)}</td>
                          <td className="py-2 px-2 text-right">{fmt(t.contactable)}</td>
                          <td className="py-2 px-2 text-right">{t.contactable_pct.toFixed(1)}%</td>
                          <td className="py-2 px-2 text-right">{fmt(t.converted)}</td>
                          <td className="py-2 px-2 text-right bg-slate-100 text-slate-900">
                            {fmt(t.total_converted)}
                            {t.total_calls > 0 && <span className="text-slate-400 text-[10px] ml-1">({t.total_conversion_pct.toFixed(1)}%)</span>}
                          </td>
                          <td className="py-2 px-2 text-right text-emerald-700">{t.conversion_of_contactable_pct.toFixed(1)}%</td>
                          <td className="py-2 px-2 text-right text-sky-700">
                            {fmt(t.sms_converted)}
                            {t.total_calls > 0 && <span className="text-slate-400 text-[10px] ml-1">({t.sms_conversion_pct.toFixed(1)}%)</span>}
                          </td>
                          <td className="py-2 px-2 text-right text-amber-700">{fmt(t.wn_converted)}</td>
                          <td className="py-2 px-2 text-right text-amber-700">{fmt(t.invalid_converted)}</td>
                          <td className="py-2 px-2 text-right text-slate-500">{t.conversion_pct.toFixed(1)}%</td>
                          <td className="py-2 px-2 text-right">{fmt(t.fresh)}/{fmt(t.followup)}</td>
                          <td className="py-2 px-2 text-right">{t.wrong_number_pct.toFixed(1)}%</td>
                        </tr>
                      </tfoot>
                    );
                  })()}
                </table>
                {!agentsOpen && hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setAgentsOpen(true)}
                    className="mt-3 text-xs text-[#0A84FF] hover:underline"
                    data-testid="agent-leaderboard-show-all"
                  >
                    Show all {agentsSorted.length} agents →
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </LoadingOverlay>

      {/* By Team — same columns as agent leaderboard, grouped by team */}
      <LoadingOverlay loading={loading}>
        <div className="glass-card p-4" data-testid="team-leaderboard-card">
          <div className="font-outfit font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#0A84FF]" />
            By team
            <span className="text-xs font-normal text-slate-500 ml-1">
              ({teamRows.length} team{teamRows.length === 1 ? "" : "s"})
            </span>
          </div>
          {teamRows.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No team activity in the current window.</div>
          ) : (
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-sm" data-testid="team-leaderboard-table">
                <thead data-sticky>
                  <tr className="text-slate-500 text-xs uppercase">
                    <SortableTh {...tThProps("team_name", "left")}>Team</SortableTh>
                    <SortableTh {...tThProps("active_agents")} title="Distinct agents who logged calls in this window">Agents</SortableTh>
                    <SortableTh {...tThProps("total_calls")}>Calls</SortableTh>
                    <SortableTh {...tThProps("contactable")}>Contactable</SortableTh>
                    <SortableTh {...tThProps("contactable_pct")} title="Contactable ÷ Calls">Contact %</SortableTh>
                    <SortableTh {...tThProps("converted")}>Converted</SortableTh>
                    <SortableTh {...tThProps("total_converted")} className="bg-slate-50" title="Total across all credit channels.">Total conv</SortableTh>
                    <SortableTh {...tThProps("conversion_of_contactable_pct")} title="Converted ÷ Contactable">Conv % (contactable)</SortableTh>
                    <SortableTh {...tThProps("sms_converted")} title="SMS-attributed deposits">SMS conv</SortableTh>
                    <SortableTh {...tThProps("wn_converted")} title="Deposits from wrong-number rows">WN → Conv</SortableTh>
                    <SortableTh {...tThProps("invalid_converted")} title="Deposits from invalid-number rows">Inv → Conv</SortableTh>
                    <SortableTh {...tThProps("conversion_pct")} title="Converted ÷ Total calls">Conv % (dialled)</SortableTh>
                    <SortableTh {...tThProps("fresh")}>Fresh / Follow</SortableTh>
                    <SortableTh {...tThProps("wrong_number_pct")}>Wrong number %</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {teamRowsSorted.map((r) => (
                    <tr key={r.team_id || r.team_name} className="border-t border-white/60" data-testid={`team-row-${r.team_id || r.team_name}`}>
                      <td className="py-2 px-2 font-medium">{r.team_name}</td>
                      <td className="py-2 px-2 text-right font-mono text-slate-700">{fmt(r.active_agents || 0)}</td>
                      <td className="py-2 px-2 text-right">{fmt(r.total_calls)}</td>
                      <td className="py-2 px-2 text-right">{fmt(r.contactable)}</td>
                      <td className="py-2 px-2 text-right">{(r.contactable_pct ?? 0).toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right">{fmt(r.converted)}</td>
                      <td className="py-2 px-2 text-right bg-slate-50 font-semibold text-slate-900" data-testid={`team-total-${r.team_id || r.team_name}`}>
                        {fmt(r.total_converted || 0)}
                        {r.total_calls > 0 && (
                          <span className="text-slate-400 text-[10px] ml-1">({(r.total_conversion_pct ?? 0).toFixed(1)}%)</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right font-semibold text-emerald-700">{(r.conversion_of_contactable_pct ?? 0).toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right text-sky-700">
                        {fmt(r.sms_converted || 0)}
                        {r.total_calls > 0 && <span className="text-slate-400 text-[10px] ml-1">({(r.sms_conversion_pct ?? 0).toFixed(1)}%)</span>}
                      </td>
                      <td className={`py-2 px-2 text-right ${(r.wn_converted || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{fmt(r.wn_converted || 0)}</td>
                      <td className={`py-2 px-2 text-right ${(r.invalid_converted || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{fmt(r.invalid_converted || 0)}</td>
                      <td className="py-2 px-2 text-right text-slate-500">{(r.conversion_pct ?? 0).toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right">{fmt(r.fresh)}/{fmt(r.followup)}</td>
                      <td className={`py-2 px-2 text-right ${r.wrong_number_pct > 30 ? "text-red-600 font-semibold" : ""}`}>{(r.wrong_number_pct ?? 0).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
                {(() => {
                  const t = computeTotals(teamRows);
                  if (!t) return null;
                  return (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="team-leaderboard-totals">
                        <td className="py-2 px-2">Total</td>
                        <td className="py-2 px-2 text-right font-mono text-slate-700">
                          {fmt(teamRows.reduce((a, r) => a + (Number(r.active_agents) || 0), 0))}
                        </td>
                        <td className="py-2 px-2 text-right">{fmt(t.total_calls)}</td>
                        <td className="py-2 px-2 text-right">{fmt(t.contactable)}</td>
                        <td className="py-2 px-2 text-right">{t.contactable_pct.toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right">{fmt(t.converted)}</td>
                        <td className="py-2 px-2 text-right bg-slate-100 text-slate-900">
                          {fmt(t.total_converted)}
                          {t.total_calls > 0 && <span className="text-slate-400 text-[10px] ml-1">({t.total_conversion_pct.toFixed(1)}%)</span>}
                        </td>
                        <td className="py-2 px-2 text-right text-emerald-700">{t.conversion_of_contactable_pct.toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right text-sky-700">
                          {fmt(t.sms_converted)}
                          {t.total_calls > 0 && <span className="text-slate-400 text-[10px] ml-1">({t.sms_conversion_pct.toFixed(1)}%)</span>}
                        </td>
                        <td className="py-2 px-2 text-right text-amber-700">{fmt(t.wn_converted)}</td>
                        <td className="py-2 px-2 text-right text-amber-700">{fmt(t.invalid_converted)}</td>
                        <td className="py-2 px-2 text-right text-slate-500">{t.conversion_pct.toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right">{fmt(t.fresh)}/{fmt(t.followup)}</td>
                        <td className="py-2 px-2 text-right">{t.wrong_number_pct.toFixed(1)}%</td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          )}
        </div>
      </LoadingOverlay>

      {/* By Campaign — same columns as agent leaderboard, grouped by campaign */}
      <LoadingOverlay loading={loading}>
        <div className="glass-card p-4" data-testid="campaign-leaderboard-card">
          <div className="font-outfit font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-[#0A84FF]" />
            By campaign
            <span className="text-xs font-normal text-slate-500 ml-1">
              ({campaignLb.length} campaign{campaignLb.length === 1 ? "" : "s"})
            </span>
          </div>
          {campaignLb.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No campaign activity in the current window.</div>
          ) : (
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-sm" data-testid="campaign-leaderboard-table">
                <thead data-sticky>
                  <tr className="text-slate-500 text-xs uppercase">
                    <SortableTh {...cThProps("campaign_name", "left")}>Campaign</SortableTh>
                    <SortableTh {...cThProps("brand_name", "left")}>Brand</SortableTh>
                    <SortableTh {...cThProps("active_agents")} title="Distinct agents who logged calls for this campaign">Agents</SortableTh>
                    <SortableTh {...cThProps("total_calls")}>Calls</SortableTh>
                    <SortableTh {...cThProps("contactable")}>Contactable</SortableTh>
                    <SortableTh {...cThProps("contactable_pct")}>Contact %</SortableTh>
                    <SortableTh {...cThProps("converted")}>Converted</SortableTh>
                    <SortableTh {...cThProps("total_converted")} className="bg-slate-50">Total conv</SortableTh>
                    <SortableTh {...cThProps("conversion_of_contactable_pct")}>Conv % (contactable)</SortableTh>
                    <SortableTh {...cThProps("sms_converted")}>SMS conv</SortableTh>
                    <SortableTh {...cThProps("wn_converted")}>WN → Conv</SortableTh>
                    <SortableTh {...cThProps("invalid_converted")}>Inv → Conv</SortableTh>
                    <SortableTh {...cThProps("conversion_pct")}>Conv % (dialled)</SortableTh>
                    <SortableTh {...cThProps("fresh")}>Fresh / Follow</SortableTh>
                    <SortableTh {...cThProps("wrong_number_pct")}>Wrong number %</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {campaignRowsSorted.map((r) => (
                    <tr key={r.campaign_id} className="border-t border-white/60" data-testid={`campaign-lb-row-${r.campaign_id}`}>
                      <td className="py-2 px-2 font-mono font-medium">{r.campaign_name}</td>
                      <td className="py-2 px-2 text-slate-600">{r.brand_name}</td>
                      <td className="py-2 px-2 text-right font-mono text-slate-700">{fmt(r.active_agents || 0)}</td>
                      <td className="py-2 px-2 text-right">{fmt(r.total_calls)}</td>
                      <td className="py-2 px-2 text-right">{fmt(r.contactable)}</td>
                      <td className="py-2 px-2 text-right">{(r.contactable_pct ?? 0).toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right">{fmt(r.converted)}</td>
                      <td className="py-2 px-2 text-right bg-slate-50 font-semibold text-slate-900" data-testid={`campaign-lb-total-${r.campaign_id}`}>
                        {fmt(r.total_converted || 0)}
                        {r.total_calls > 0 && (
                          <span className="text-slate-400 text-[10px] ml-1">({(r.total_conversion_pct ?? 0).toFixed(1)}%)</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right font-semibold text-emerald-700">{(r.conversion_of_contactable_pct ?? 0).toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right text-sky-700">
                        {fmt(r.sms_converted || 0)}
                        {r.total_calls > 0 && <span className="text-slate-400 text-[10px] ml-1">({(r.sms_conversion_pct ?? 0).toFixed(1)}%)</span>}
                      </td>
                      <td className={`py-2 px-2 text-right ${(r.wn_converted || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{fmt(r.wn_converted || 0)}</td>
                      <td className={`py-2 px-2 text-right ${(r.invalid_converted || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{fmt(r.invalid_converted || 0)}</td>
                      <td className="py-2 px-2 text-right text-slate-500">{(r.conversion_pct ?? 0).toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right">{fmt(r.fresh)}/{fmt(r.followup)}</td>
                      <td className={`py-2 px-2 text-right ${r.wrong_number_pct > 30 ? "text-red-600 font-semibold" : ""}`}>{(r.wrong_number_pct ?? 0).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
                {(() => {
                  const t = computeTotals(campaignLb);
                  if (!t) return null;
                  return (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="campaign-leaderboard-totals">
                        <td className="py-2 px-2">Total</td>
                        <td className="py-2 px-2 text-slate-400">—</td>
                        <td className="py-2 px-2 text-right font-mono text-slate-700">
                          {fmt(campaignLb.reduce((a, r) => a + (Number(r.active_agents) || 0), 0))}
                        </td>
                        <td className="py-2 px-2 text-right">{fmt(t.total_calls)}</td>
                        <td className="py-2 px-2 text-right">{fmt(t.contactable)}</td>
                        <td className="py-2 px-2 text-right">{t.contactable_pct.toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right">{fmt(t.converted)}</td>
                        <td className="py-2 px-2 text-right bg-slate-100 text-slate-900">
                          {fmt(t.total_converted)}
                          {t.total_calls > 0 && <span className="text-slate-400 text-[10px] ml-1">({t.total_conversion_pct.toFixed(1)}%)</span>}
                        </td>
                        <td className="py-2 px-2 text-right text-emerald-700">{t.conversion_of_contactable_pct.toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right text-sky-700">
                          {fmt(t.sms_converted)}
                          {t.total_calls > 0 && <span className="text-slate-400 text-[10px] ml-1">({t.sms_conversion_pct.toFixed(1)}%)</span>}
                        </td>
                        <td className="py-2 px-2 text-right text-amber-700">{fmt(t.wn_converted)}</td>
                        <td className="py-2 px-2 text-right text-amber-700">{fmt(t.invalid_converted)}</td>
                        <td className="py-2 px-2 text-right text-slate-500">{t.conversion_pct.toFixed(1)}%</td>
                        <td className="py-2 px-2 text-right">{fmt(t.fresh)}/{fmt(t.followup)}</td>
                        <td className="py-2 px-2 text-right">{t.wrong_number_pct.toFixed(1)}%</td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          )}
        </div>
      </LoadingOverlay>

      {/* Heatmap */}
      <LoadingOverlay loading={loading}>
        <div className="glass-card p-4 overflow-x-auto">
          <div className="font-outfit font-semibold text-slate-900 mb-3">Disposition heatmap (day × disposition)</div>
          {heat.dates.length === 0 ? (
            <div className="text-sm text-slate-500">No data.</div>
          ) : (
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="text-left py-2 pr-3 text-slate-500">Date</th>
                  {heat.dispositions.map((dp) => <th key={dp} className="text-left py-2 pr-3 text-slate-500 whitespace-nowrap">{dp}</th>)}
                </tr>
              </thead>
              <tbody>
                {heat.dates.map((d) => (
                  <tr key={d} className="border-t border-white/60">
                    <td className="py-2 pr-3 font-medium">{d}</td>
                    {heat.dispositions.map((dp) => {
                      const v = heat.grid[d]?.[dp] || 0;
                      const alpha = v ? Math.min(0.6, 0.1 + (v / heatMax) * 0.5) : 0;
                      return (
                        <td key={dp} className="py-1.5 pr-1.5">
                          <div className="rounded-md px-2 py-1 min-w-[36px] text-center"
                               style={{ background: `rgba(10,132,255,${alpha})` }}>
                            {v || "—"}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </LoadingOverlay>

      {/* Slide-out drill-down for any converted-count cell above.
          `drilldown.open` is toggled from KPI cards + leaderboard cells. */}
      <ConvertedUsersDrilldown
        state={drilldown}
        onClose={() => setDrilldown({ open: false })}
      />
    </div>
  );
}
